import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Fuel,
  Flame,
  Wrench,
  Landmark,
  TrafficCone,
  Factory,
  FlaskConical,
  Utensils,
  Warehouse,
  Boxes,
  Truck,
  HardHat,
  Anchor,
  Plane,
  TrainFront,
  Pickaxe,
  Droplets,
  Waves,
  SprayCan,
  Siren,
  CloudRain,
  ClipboardCheck,
  Package,
  GraduationCap,
  Settings,
  ArrowRight,
} from "lucide-react";
import { industriesData, solutionsPool } from "../mock";

const iconMap = {
  Fuel,
  Flame,
  Wrench,
  Landmark,
  TrafficCone,
  Factory,
  FlaskConical,
  Utensils,
  Warehouse,
  Boxes,
  Truck,
  HardHat,
  Anchor,
  Plane,
  TrainFront,
  Pickaxe,
  Droplets,
  Waves,
  SprayCan,
  Siren,
  CloudRain,
  ClipboardCheck,
  Package,
  GraduationCap,
  Settings,
};

const filters = [
  { id: "all", label: "All" },
  { id: "Product", label: "Products" },
  { id: "Service", label: "Solutions" },
];

export default function IndustriesExplorer() {
  const [activeIndex, setActiveIndex] = useState(0);
  const [filter, setFilter] = useState("all");

  const industry = industriesData[activeIndex];
  const resolved = industry.solutions
    .map((id) => ({ id, ...solutionsPool[id] }))
    .filter((s) => filter === "all" || s.type === filter);

  return (
    <section className="py-24 msv-dot-bg">
      <div className="mx-auto max-w-[1240px] px-6">
        <div className="text-center mb-14">
          <p className="msv-eyebrow mb-4">Tailored By Sector</p>
          <h2 className="font-heading text-3xl md:text-4xl uppercase tracking-[0.08em] text-[#1a2540]">
            Industries We Serve
          </h2>
          <p className="text-[#6a6a6a] leading-relaxed mt-5 max-w-2xl mx-auto">
            Select your industry to explore the products and solutions Market Street Ventures
            tailors to your operation.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[340px_1fr] gap-8">
          {/* Industry selector */}
          <div className="grid grid-cols-2 lg:grid-cols-2 gap-2.5 content-start">
            {industriesData.map((ind, i) => {
              const Icon = iconMap[ind.icon] || Factory;
              const active = i === activeIndex;
              return (
                <button
                  key={ind.name}
                  onClick={() => setActiveIndex(i)}
                  className={`group relative flex items-center gap-3 h-14 pl-4 pr-3 rounded-xl text-left border overflow-hidden transition-all duration-300 ${
                    active
                      ? "bg-gradient-to-br from-[#1a2540] to-[#0f2c4a] border-[#1a2540] text-white shadow-[0_14px_28px_-12px_rgba(15,41,74,0.7)] -translate-y-0.5"
                      : "bg-white border-gray-200 text-[#4a4a4a] hover:border-[#1b75bc]/50 hover:text-[#1a2540] hover:shadow-[0_10px_22px_-14px_rgba(15,41,74,0.4)] hover:-translate-y-0.5"
                  }`}
                >
                  <span
                    className={`absolute left-0 top-0 h-full w-1 transition-colors duration-300 ${
                      active ? "bg-[#4aa3e0]" : "bg-transparent group-hover:bg-[#1b75bc]/40"
                    }`}
                  />
                  <span
                    className={`flex items-center justify-center w-8 h-8 rounded-lg shrink-0 transition-colors duration-300 ${
                      active
                        ? "bg-white/10 text-[#4aa3e0]"
                        : "bg-[#eef4fb] text-[#1b75bc] group-hover:bg-[#e0edf9]"
                    }`}
                  >
                    <Icon size={16} />
                  </span>
                  <span className="text-[11px] font-medium leading-[1.15]">{ind.name}</span>
                </button>
              );
            })}
          </div>

          {/* Detail panel */}
          <div key={industry.name} className="bg-white rounded-2xl border border-gray-100 shadow-[0_20px_45px_-25px_rgba(15,41,74,0.35)] p-8 md:p-10 animate-fade-up">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-6 mb-8">
              <div className="flex items-start gap-4">
                {(() => {
                  const Icon = iconMap[industry.icon] || Factory;
                  return (
                    <div className="msv-icon w-14 h-14 shrink-0">
                      <Icon size={26} />
                    </div>
                  );
                })()}
                <div>
                  <h3 className="font-heading text-2xl uppercase tracking-[0.05em] text-[#1a2540]">
                    {industry.name}
                  </h3>
                  <p className="text-sm text-[#6a6a6a] leading-relaxed mt-2 max-w-xl">
                    {industry.blurb}
                  </p>
                </div>
              </div>

              {/* Filter toggle */}
              <div className="flex bg-[#f6f8fa] rounded-xl border border-gray-200 p-1 shrink-0 self-start">
                {filters.map((f) => (
                  <button
                    key={f.id}
                    onClick={() => setFilter(f.id)}
                    className={`font-heading uppercase tracking-[0.1em] text-xs px-3.5 py-2 rounded-lg transition-all duration-200 ${
                      filter === f.id ? "bg-[#1b75bc] text-white shadow-sm" : "text-[#6a6a6a] hover:text-[#1a2540]"
                    }`}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {resolved.map((s) => {
                const Icon = iconMap[s.icon] || Package;
                return (
                  <Link
                    key={s.id}
                    to={s.link}
                    className="group relative flex gap-4 p-5 rounded-xl border border-gray-100 bg-white overflow-hidden hover:border-[#1b75bc]/40 hover:shadow-[0_20px_38px_-20px_rgba(15,41,74,0.42)] hover:-translate-y-1 transition-all duration-300"
                  >
                    <span className="absolute left-0 top-0 h-full w-1 bg-transparent group-hover:bg-[#1b75bc] transition-colors duration-300" />
                    <div className="msv-icon w-11 h-11 shrink-0 transition-transform duration-300 group-hover:scale-110">
                      <Icon size={20} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-start justify-between gap-2 mb-1">
                        <h4 className="font-heading text-sm uppercase tracking-[0.03em] text-[#1a2540] leading-tight group-hover:text-[#1b75bc] transition-colors duration-300">
                          {s.name}
                        </h4>
                        <ArrowRight
                          size={15}
                          className="text-[#1b75bc] shrink-0 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300"
                        />
                      </div>
                      <span
                        className={`inline-block text-[10px] font-heading uppercase tracking-[0.1em] px-2 py-0.5 mb-2 rounded ${
                          s.type === "Product"
                            ? "bg-[#e8f2fa] text-[#1b75bc]"
                            : "bg-[#eef1f4] text-[#1a2540]"
                        }`}
                      >
                        {s.type}
                      </span>
                      <p className="text-xs text-[#6a6a6a] leading-relaxed">{s.desc}</p>
                    </div>
                  </Link>
                );
              })}
            </div>

            {resolved.length === 0 && (
              <p className="text-sm text-[#6a6a6a] text-center py-8">
                No {filter === "Product" ? "products" : "solutions"} in this category for {industry.name}.
              </p>
            )}

            <div className="mt-8 pt-6 border-t border-gray-100 flex flex-col sm:flex-row items-center justify-between gap-4">
              <p className="text-sm text-[#6a6a6a]">
                Need a program tailored to your {industry.name.toLowerCase()} operation?
              </p>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 font-heading uppercase tracking-[0.16em] text-sm text-white bg-[#1a2540] px-6 py-3 hover:bg-[#1b75bc] transition-colors duration-300"
              >
                Talk To Us <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
